import { cn, formatValueOrDefault } from '@/utils/utils.ts';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useMemo, useRef, useState } from 'react';
import { calculateDateDifference } from '@/utils/compare-dates.ts';
import { format, isThisYear, isToday, isTomorrow } from 'date-fns';
import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { DateRangePicker } from '@/components/ui/date-range-picker.tsx';
import { useGetBMSStatusDeviceQuery } from '@/features/devices/hooks/queries/[device]/get-bms-status-device.ts';
import { useGetChartDeviceQuery } from '@/features/devices/hooks/queries/[device]/get-charts-device.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useTranslation } from 'react-i18next';
import { convertEnergyFromKWh } from '@/utils/format-metric-value.ts';

type TranslationKey =
  | 'devices.select.parameters.batteryCharge'
  | 'devices.select.parameters.batteryDischarge'
  | 'devices.select.parameters.batteryStateOfCharge';

export function BatteryOverviewSection({ deviceId }: { deviceId: string | undefined }) {
  const { data } = useGetBMSStatusDeviceQuery({
    deviceId,
    moduleNumber: undefined,
  });
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <div className="min-h-8">
        <h2 className="text-lg font-medium">{t('devices.batteryModule.realTimeData')}</h2>
      </div>
      <div className="flex h-full flex-col gap-4">
        <div>
          <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <li className="grid grid-rows-2">
              <span className="text-muted-foreground text-sm text-nowrap">
                {t('devices.select.parameters.stateOfCharge')}
              </span>
              <span className="font-medium">
                {formatValueOrDefault(data?.stateOfCharge, '%')}{' '}
              </span>
            </li>
            <li className="grid grid-rows-2">
              <span className="text-muted-foreground text-sm text-nowrap">
                {t('devices.select.parameters.batteryVoltage')}
              </span>
              <span className="font-medium">
                {formatValueOrDefault(data?.batteryVoltage, 'V')}{' '}
              </span>
            </li>
            <li className="grid grid-rows-2">
              <span className="text-muted-foreground text-sm text-nowrap">
                {t('devices.select.parameters.batteryCurrent')}
              </span>
              <span className="font-medium">
                {formatValueOrDefault(data?.batteryCurrent, 'A')}{' '}
              </span>
            </li>
          </ul>
        </div>
        <div>
          <ul>
            <li className="grid grid-rows-2">
              <span className="text-muted-foreground text-sm text-nowrap">
                {t('devices.select.parameters.batteryTemperature')}
              </span>
              <span className="font-medium">
                {formatValueOrDefault(data?.batteryTemperature, '°C')}{' '}
              </span>
            </li>
          </ul>
        </div>
        <div>
          <ul className="grid grid-cols-2 gap-4">
            <li className="grid grid-rows-2">
              <span className="text-muted-foreground text-sm text-nowrap">
                {t('devices.select.parameters.totalChargedEnergy')}
              </span>
              <span className="font-medium">
                {convertEnergyFromKWh(data?.chargingCycle).label}{' '}
              </span>
            </li>
            <li className="grid grid-rows-2">
              <span className="text-muted-foreground text-sm text-nowrap">
                {t('devices.select.parameters.totalDischargedEnergy')}
              </span>
              <span className="font-medium">
                {convertEnergyFromKWh(data?.dischargingCycle).label}
              </span>
            </li>
            <li className="grid grid-rows-2">
              <span className="text-muted-foreground text-sm text-nowrap">
                {t('devices.select.parameters.batteryStateOfHealth')}
              </span>
              <span className="font-medium">
                {formatValueOrDefault(data?.stateOfHealth, '%')}{' '}
              </span>
            </li>
            <li className="grid grid-rows-2">
              <span className="text-muted-foreground text-sm text-nowrap">
                {t('devices.batteryModule.balancingMode')}
              </span>
              <span className="font-medium">{formatValueOrDefault(data?.balancingMode)}</span>
            </li>
          </ul>
        </div>
        <EnergyManagement deviceId={deviceId} />
      </div>
    </div>
  );
}

function EnergyManagement({ deviceId }: { deviceId: string | undefined }) {
  const isMobile = useMediaQuery('(max-width:640px)');
  const defaultGranularity: RangeGranularity = '5min';
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  }>({
    startDate: new Date(new Date().setHours(0, 0, 0, 0)),
    endDate: new Date(new Date().setHours(23, 59, 59, 999)),
    rangeGranularity: defaultGranularity,
  });

  const { data, isFetching, isLoading } = useGetChartDeviceQuery({
    deviceId,
    dateRange,
    enabled: Boolean(deviceId),
  });

  const areAnyPointsAvailable =
    Boolean([data]?.length) &&
    data?.some((d) => {
      return (
        d?.Battery_Discharge != null ||
        d?.Battery_Charge != null ||
        d?.Battery_State_of_Charge != null
      );
    });

  return (
    <div className={cn(`flex h-full w-full flex-col gap-2`)}>
      <div className="flex flex-col justify-between gap-2 sm:flex-row">
        <h3 className="font-semibold text-nowrap">{t('titles.graph_energy_management')}</h3>
        <div className={cn(isFetching && 'pointer-events-none animate-pulse opacity-60')}>
          <DateRangePicker
            className="h-8"
            onUpdate={(values) => {
              setDateRange((current) => ({
                ...current,
                startDate: values.range.from,
                endDate: values.range.to,
                rangeGranularity: values.rangeGranularity as RangeGranularity,
              }));
            }}
            initialDateFrom={dateRange.startDate}
            initialDateTo={dateRange.endDate}
            locale="ro-RO"
            defaultGranularity={defaultGranularity}
          />
        </div>
      </div>
      {isLoading && (
        <div
          className={cn(
            'flex h-full w-full items-center justify-center',
            isMobile ? 'min-h-[248px]' : 'min-h-[238px]'
          )}
        >
          <LoadingSpinner className="size-14" />
        </div>
      )}
      {!isLoading && !areAnyPointsAvailable && (
        <div
          className={cn(
            'flex h-full w-full items-center justify-center rounded-md bg-gray-50',
            isMobile ? 'min-h-[248px]' : 'min-h-[238px]'
          )}
        >
          <span className="text-muted-foreground">
            {t('devices.batteryModule.noDataFound')}
          </span>
        </div>
      )}
      {!isLoading && areAnyPointsAvailable && (
        <EnergyManagementChart
          data={data ?? []}
          rangeGranularity={dateRange.rangeGranularity}
          className={cn(isFetching && 'pointer-events-none animate-pulse opacity-60')}
        />
      )}
    </div>
  );
}

export const CHART_COLORS = {
  Battery_Charge: '#038BA8',
  Battery_Discharge: '#AC44FB',
  Battery_State_of_Charge: '#AC99FB',
} as const;

const DEFAULT_CHART_FIELDS = [
  {
    label: 'devices.select.parameters.batteryCharge',
    color: CHART_COLORS.Battery_Charge,
    selected: true,
    dataKey: 'Battery_Charge',
    axis: false,
    id: 'Battery_Charge',
  },
  {
    label: 'devices.select.parameters.batteryDischarge',
    color: CHART_COLORS.Battery_Discharge,
    selected: true,
    dataKey: 'Battery_Discharge',
    axis: false,
    id: 'Battery_Discharge',
  },
  {
    label: 'devices.select.parameters.batteryStateOfCharge',
    color: CHART_COLORS.Battery_State_of_Charge,
    selected: true,
    dataKey: 'Battery_State_of_Charge',
    axis: false,
    id: 'Battery_State_of_Charge',
  },
];

type RangeGranularity = '5min' | 'hourly' | 'daily' | 'monthly';
type ChartProps = {
  data?: {
    Battery_Charge: number;
    Battery_Discharge: number;
    Battery_State_of_Charge: number;
    time: string;
  }[];
  rangeGranularity: RangeGranularity;
  className?: string;
};

const shouldUseBars = (granularity: RangeGranularity): boolean => {
  return ['hourly', 'daily', 'monthly'].includes(granularity);
};

function EnergyManagementChart({ data, rangeGranularity, className }: ChartProps) {
  const isMobile = useMediaQuery('(max-width:640px)');
  const chartRef = useRef<HTMLDivElement>(null);
  const [fields, setFields] = useState(DEFAULT_CHART_FIELDS);
  const { t } = useTranslation();

  const chartDate = useMemo(() => {
    if (!data) return [];

    return data.map((v) => {
      return {
        Battery_Charge: v?.Battery_Charge !== null ? Math.abs(v?.Battery_Charge) : null,
        Battery_Discharge:
          v?.Battery_Discharge === null
            ? null
            : shouldUseBars(rangeGranularity)
              ? -Math.abs(v?.Battery_Discharge)
              : Math.abs(v?.Battery_Discharge),
        Battery_State_of_Charge:
          (rangeGranularity === '5min' || rangeGranularity === 'hourly') &&
          v?.Battery_State_of_Charge !== null
            ? +(v?.Battery_State_of_Charge || 0).toFixed(2)
            : null,
        time: v?.time,
      };
    });
  }, [data, rangeGranularity]);

  const dateDifference = calculateDateDifference(
    new Date(data?.at(0)?.time ?? ''),
    new Date(data?.at(-1)?.time ?? '')
  );

  const translatedFields = useMemo(
    () =>
      fields.map((field) => ({
        ...field,
        translatedLabel: t(field.label as TranslationKey),
        label: field.label,
      })),
    [t, fields]
  );

  const filteredField = translatedFields.filter(
    (f) =>
      !(
        (rangeGranularity === 'daily' || rangeGranularity === 'monthly') &&
        f.id === 'Battery_State_of_Charge'
      )
  );

  return (
    <div
      className={cn(
        className,
        'flex w-full flex-col gap-2 select-none',
        isMobile ? 'min-h-[248px]' : 'min-h-[238px]'
      )}
    >
      <div className={cn(`relative w-full`, isMobile ? 'h-[180px]' : 'h-[200px]')}>
        <div className="absolute top-3 right-12 z-10"></div>
        <ChartContainer config={{}} className="h-full w-full">
          <div className="h-full" ref={chartRef} style={{ touchAction: 'none' }}>
            <div className="absolute top-3 right-3 z-10 flex justify-end sm:mb-4"></div>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartDate}
                margin={{
                  top: 4,
                  right: isMobile ? -15 : 0,
                  bottom: 0,
                  left: isMobile ? -15 : 0,
                }}
              >
                <defs>
                  {fields.map((v) => (
                    <linearGradient key={v.id} id={v.id} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={v.color} stopOpacity={1} />
                      <stop offset="95%" stopColor={v.color} stopOpacity={0.2} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  key="xAxis-main"
                  dataKey="time"
                  xAxisId="main"
                  tickFormatter={(t) => {
                    return formatDate(t, rangeGranularity, dateDifference);
                  }}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  minTickGap={40}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <YAxis
                  yAxisId="left"
                  label={{
                    value: rangeGranularity === '5min' ? 'kW' : 'kWh',
                    angle: -90,
                    position: 'left',
                    offset: isMobile ? -20 : -10,
                  }}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{
                    value: '%',
                    angle: -90,
                    position: 'right',
                    offset: isMobile ? -20 : -10,
                  }}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <ChartTooltip
                  cursor={false}
                  content={(props) => (
                    <ChartTooltipComponent
                      {...props}
                      dateDifference={dateDifference}
                      rangeGranularity={rangeGranularity}
                    />
                  )}
                />
                {fields.map((v, index) => {
                  if (!v.selected) return null;

                  const hasOneRecord = isMobile
                    ? chartDate?.length <= 4
                    : chartDate?.length <= 7;

                  const translatedName = t(v.label as TranslationKey);

                  if (shouldUseBars(rangeGranularity)) {
                    if (v.dataKey === 'Battery_State_of_Charge') {
                      return (
                        <Area
                          xAxisId="main"
                          yAxisId={v.id === 'Battery_State_of_Charge' ? 'right' : 'left'}
                          name={translatedName}
                          key={`area-${v.dataKey}-${index}`}
                          dataKey={v.dataKey}
                          stroke={v.color}
                          fill={`url(#${v.id})`}
                          fillOpacity={0.3}
                        />
                      );
                    }

                    return [
                      hasOneRecord ? null : (
                        <XAxis key={`xAxis-${index}`} dataKey="time" xAxisId={index} hide />
                      ),
                      <Bar
                        key={`bar-${v.dataKey}-${index}`}
                        xAxisId={hasOneRecord ? 'main' : index}
                        yAxisId={v.id === 'Battery_State_of_Charge' ? 'right' : 'left'}
                        dataKey={v.dataKey}
                        name={translatedName}
                        fill={`${v.color}80`}
                        stroke={v.color}
                        strokeWidth={1}
                        radius={[10, 10, 0, 0]}
                      />,
                    ];
                  } else {
                    return (
                      <Area
                        xAxisId="main"
                        yAxisId={v.id === 'Battery_State_of_Charge' ? 'right' : 'left'}
                        name={translatedName}
                        key={`area-${v.dataKey}-${index}`}
                        dataKey={v.dataKey}
                        stroke={v.color}
                        fill={`url(#${v.id})`}
                        fillOpacity={0.3}
                      />
                    );
                  }
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ChartContainer>
      </div>
      <div className="mx-auto flex flex-wrap gap-1 md:gap-1">
        {filteredField.map((f) => {
          return (
            <Button
              key={f.dataKey}
              variant="ghost"
              style={{ opacity: !f.selected ? '0.4' : '1' }}
              className="h-min w-min justify-start p-1 px-2"
              onClick={() => {
                setFields((current) =>
                  current.map((c) => {
                    if (c.label === f.label)
                      return {
                        ...c,
                        selected: !c.selected,
                      };

                    return c;
                  })
                );
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-4 rounded-full"
                  style={{ backgroundColor: f.color }}
                ></span>
                <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                  {f.translatedLabel}
                </span>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

const ChartTooltipComponent = ({
  payload,
  label,
  rangeGranularity,
  dateDifference,
}: TooltipContentProps<ValueType, NameType> & {
  rangeGranularity: '5min' | 'hourly' | 'daily' | 'monthly' | undefined;
  dateDifference: {
    days: number;
    weeks: number;
    months: number;
    years: number;
  };
}) => {
  const formattedDate = label ? formatDate(new Date(label), rangeGranularity, dateDifference) : '';

  const formatValue = (p: Payload<ValueType, NameType>): string => {
    if (p?.dataKey && p.dataKey === 'Battery_State_of_Charge') return `${p.value} %`;

    if (!p?.value && rangeGranularity === '5min') return '0 kW';
    if (!p?.value) return '0 kWh';

    const value = Number(p.value);
    const fixedValue = +value.toFixed(2);
    const isBatteryPower = p.name === 'Battery Power';

    if (rangeGranularity === '5min') {
      return `${Math.abs(value).toFixed(2)} kW`;
    }

    if (value < 1000) {
      return isBatteryPower ? `${fixedValue} kWh` : `${Math.abs(fixedValue)} kWh`;
    }

    const megaWattValue = (value / 1000).toFixed(2);
    return isBatteryPower ? `${megaWattValue} MWh` : `${Math.abs(Number(megaWattValue))} MWh`;
  };

  return (
    <div className="flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>{formattedDate}</p>
      <div className="mt-2">
        {payload?.map((p: Payload<ValueType, NameType>) => (
          <p style={{ color: p.stroke }} key={p.name}>
            {p.name} : {formatValue(p)}
          </p>
        ))}
      </div>
    </div>
  );
};

function formatDate(
  date: Date,
  rangeGranularity?: RangeGranularity,
  dateDifference?: {
    days: number;
    weeks: number;
    months: number;
    years: number;
  }
): string {
  if (!rangeGranularity || !dateDifference) return '';

  const { days, weeks, months, years } = dateDifference;

  if (rangeGranularity === '5min' || rangeGranularity === 'hourly') {
    if ((isToday(date) && days <= 1) || (isTomorrow(date) && days <= 1) || days <= 1) {
      return format(date, 'HH:mm');
    } else {
      return format(date, 'MMM d, HH:mm');
    }
  } else if (rangeGranularity === 'daily') {
    if (weeks < 2) {
      return format(date, 'EEE, MMM d');
    } else if (months < 2) {
      return format(date, 'MMM d');
    } else if (isThisYear(date)) {
      return format(date, 'MMM d');
    } else {
      return format(date, 'MMM d, yyyy');
    }
  } else if (rangeGranularity === 'monthly') {
    if (years < 1) {
      return format(date, 'MMM');
    } else {
      return format(date, 'MMM yyyy');
    }
  }

  // Default fallback
  return format(date, 'yyyy-MM-dd HH:mm');
}
